import React from 'react';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Flatpickr from 'react-flatpickr';
import ParticipantIdentity from '../shared/ParticipantIdentity';
import Select2Wrapper from '../Select2Wrapper';
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import LockIcon from '@material-ui/icons/Lock';
import { PARTICIPANT_STATUSES } from '../../constants/ehc/ParticipantConstants';

const toDateInput = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

const earliest = (...dates) => dates.filter(Boolean).sort()[0];

export default class EhcParticipantCheckIn extends React.Component {
  state = {
    error: null,
    submitting: false,
    form: null,
    hospitalOptions: [],
    doctorOptions: [],
    doctorsHospitalId: null
  };

  baseUrl = (company_id) => `/api/ehc/companies/${company_id}/participants`;

  participant = () => (this.props.participants || [])[0];

  // A participant who never scheduled themselves arrives with nothing set, so the counter
  // supplies the whole visit here instead of walking the edit and confirm modals first.
  isWalkIn = () => {
    const participant = this.participant();
    return !!participant && participant.status !== PARTICIPANT_STATUSES.CONFIRMED;
  };

  labels = () => this.props.labels?.check_in || {};

  parseError(result) {
    if (result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors)) {
      return 'Validation: ' + Object.entries(result.errors).map(([k, v]) => `${k}: ${v}`).join(', ');
    }
    if (result.errors && typeof result.errors === 'string') return result.errors;
    if (Array.isArray(result.errors) && result.errors.length > 0) return result.errors.join(', ');
    if (result.message) return result.message;
    return 'An error occurred.';
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && (!prevProps.open || prevProps.participants !== this.props.participants)) {
      this.seedForm();
    }

    if (this.props.hospitals !== prevProps.hospitals) {
      this.setState({ hospitalOptions: (this.props.hospitals || []).map(([name, id]) => ({ value: id, label: name })) });
    }

    if (this.props.open && this.isWalkIn()) {
      this.loadDoctorsIfNeeded(this.state.form?.hospital_id);
    }
  }

  seedForm = () => {
    const participant = this.participant();
    if (!participant) return;

    this.setState({
      error: null,
      submitting: false,
      hospitalOptions: (this.props.hospitals || []).map(([name, id]) => ({ value: id, label: name })),
      form: {
        hospital_id: this.props.lockedHospitalId || participant.hospital_id || '',
        visit_date: participant.visit_date || this.defaultVisitDate(),
        confirmed_datetime: participant.confirmed_datetime || new Date().toISOString(),
        doctor_ids: participant.doctor_ids || [],
        notes: participant.notes || ''
      }
    });
  };

  // A walk-in happened today, so the picker never offers a later date - the participant is
  // being marked present at this moment.
  // ponytail: picker limits disabled on request (backend guards commented out too).
  // Restore the body below to re-enable start_date..min(today, end_date).
  visitDateLimits = () => ({ minDate: null, maxDate: null });

  defaultVisitDate = () => {
    const { minDate, maxDate } = this.visitDateLimits();
    const today = toDateInput(new Date());
    if (minDate && today < minDate) return minDate;
    if (maxDate && today > maxDate) return maxDate;
    return today;
  };

  fetchDoctors = async (hospitalId) => {
    try {
      const response = await fetch(`/api/doctors?hospital_id=${hospitalId}`);
      if (response.status >= 400) return [];
      return await response.json();
    } catch (err) {
      console.error('Fetch doctors error:', err);
      return [];
    }
  };

  loadDoctorsIfNeeded = async (hospitalId) => {
    if (this.state.doctorsHospitalId === hospitalId) return;

    if (!hospitalId) {
      this.setState({ doctorOptions: [], doctorsHospitalId: hospitalId });
      return;
    }

    const doctors = await this.fetchDoctors(hospitalId);
    this.setState({
      doctorOptions: doctors.map(doctor => ({ value: doctor.id, label: doctor.full_name })),
      doctorsHospitalId: hospitalId
    });
  };

  handleChange = (changes) => {
    this.setState(prevState => ({ form: { ...prevState.form, ...changes } }));
  };

  lockedHospitalName = () => {
    const match = (this.props.hospitals || []).find(([, id]) => id === this.props.lockedHospitalId);
    return match ? match[0] : this.labels().your_branch;
  };

  handleCheckIn = async (event) => {
    event.preventDefault();

    const participant = this.participant();
    if (!this.props.editable || !participant || this.state.submitting) return;

    const form = this.state.form || {};
    const walkIn = this.isWalkIn();
    const csrfToken = document.querySelector('[name=csrf-token]').content;

    this.setState({ submitting: true });

    try {
      const response = await fetch(
        this.baseUrl(participant.company_id) + '/' + participant.id + '/check_in',
        {
          method: 'POST',
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            'X-CSRF-Token': csrfToken
          },
          body: walkIn ? JSON.stringify({
            hospital_id: form.hospital_id ? +form.hospital_id : null,
            visit_date: form.visit_date,
            confirmed_datetime: form.confirmed_datetime,
            doctor_ids: form.doctor_ids || [],
            notes: form.notes
          }) : undefined
        }
      );

      const result = await response.json();

      if (!response.ok) {
        this.setState({ error: this.parseError(result), submitting: false });
        return;
      }

      this.setState({ error: null, submitting: false });
      this.props.onClose();
      if (this.props.tableRef.current) {
        this.props.tableRef.current.onQueryChange();
      }
    } catch (err) {
      this.setState({ error: 'An unexpected error occurred.', submitting: false });
    }
  };

  renderScheduleFields() {
    const labels = this.labels();
    const form = this.state.form || {};
    const { minDate, maxDate } = this.visitDateLimits();

    return (
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="hospital_id">{labels.hospital} *</label>
            {this.props.lockedHospitalId ? (
              <>
                <input
                  type="text"
                  className="form-control form-modal__locked-field"
                  disabled
                  readOnly
                  value={this.lockedHospitalName() || ''}
                />
                <small className="text-muted form-modal__lock-hint">
                  <LockIcon fontSize="inherit" className="mr-1" />
                  {labels.hospital_locked_hint}
                </small>
              </>
            ) : (
              <>
                <Select2Wrapper
                  required="required"
                  className="form-control"
                  value={form.hospital_id ? form.hospital_id.toString() : ''}
                  name="hospital_id"
                  onChange={(e) => this.handleChange({ hospital_id: +e.target.value, doctor_ids: [] })}
                  useDirectParent={true}
                  options={{
                    placeholder: labels.search_hospital,
                    disabled: !this.props.editable,
                    allowClear: true
                  }}
                  items={this.state.hospitalOptions}
                >
                </Select2Wrapper>
                <i className="form-group__bar"></i>
              </>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="visit_date">{labels.visit_date} *</label>
            <Flatpickr
              required
              placeholder="YYYY-MM-DD"
              disabled={!this.props.editable}
              className="form-control"
              value={form.visit_date || ''}
              onChange={(selectedDates, dateStr) => this.handleChange({ visit_date: dateStr })}
              options={{ dateFormat: "Y-m-d", allowInput: true, minDate: minDate, maxDate: maxDate }}
            />
            <i className="form-group__bar"></i>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="confirmed_datetime">{labels.confirmed_datetime} *</label>
            <Flatpickr
              required
              placeholder="YYYY-MM-DD HH:mm"
              disabled={!this.props.editable}
              className="form-control"
              value={form.confirmed_datetime || ''}
              onChange={([selectedDate]) => this.handleChange({ confirmed_datetime: selectedDate ? selectedDate.toISOString() : null })}
              options={{ dateFormat: "Y-m-d H:i", enableTime: true, time_24hr: true, minuteIncrement: 1, allowInput: true }}
            />
            <i className="form-group__bar"></i>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="doctor_ids">{labels.doctors}</label>
            <Select2Wrapper
              className="form-control"
              value={(form.doctor_ids || []).map(id => id.toString())}
              name="doctor_ids"
              onChange={(e) => this.handleChange({
                doctor_ids: e.target.selectedOptions ? Array.from(e.target.selectedOptions, option => +option.value) : []
              })}
              useDirectParent={true}
              options={{
                multiple: true,
                disabled: !this.props.editable,
                allowClear: true,
                closeOnSelect: false,
                placeholder: labels.search_doctors
              }}
              items={this.state.doctorOptions}
            >
            </Select2Wrapper>
            <i className="form-group__bar"></i>
            <small className="text-muted">{labels.doctors_branch_hint || labels.doctors_hint}</small>
          </div>
        </div>

        <div className="col-12">
          <div className="form-group">
            <label htmlFor="notes">{labels.notes}</label>
            <textarea
              placeholder={labels.notes_placeholder || labels.notes}
              disabled={!this.props.editable}
              className="form-control form-modal__notes"
              value={form.notes || ''}
              name="notes"
              onChange={(e) => this.handleChange({ notes: e.target.value })}
            />
            <i className="form-group__bar"></i>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const labels = this.labels();
    const walkIn = this.isWalkIn();
    const participants = this.props.participants || [];

    return (
      <Modal
        open={this.props.open}
        onClose={this.props.onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        style={{ overflow: 'auto' }}
      >
        <Fade in={this.props.open} timeout={500}>
          <Box className="form-modal">
            <form onSubmit={this.handleCheckIn}>
              <div className="form-modal__head">
                <Typography variant="h6" className="mb-0">
                  {walkIn ? labels.walk_in_title : labels.title}
                </Typography>
                <IconButton size="small" aria-label={labels.close} onClick={this.props.onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <div className="form-modal__body">
                <p className="text-muted">{walkIn ? labels.walk_in_prompt : labels.prompt}</p>

                {participants.map(participant => (
                  <ParticipantIdentity key={participant.id} participant={participant} labels={labels} />
                ))}

                {walkIn && this.renderScheduleFields()}

                {this.state.error && (
                  <div className="alert alert-danger" role="alert">
                    {this.state.error}
                  </div>
                )}
              </div>

              <div className="form-modal__foot">
                <Button variant="outlined" onClick={this.props.onClose}>
                  {labels.cancel}
                </Button>
                {this.props.editable &&
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={participants.length < 1 || this.state.submitting}
                    className="ml-2"
                    type="submit"
                  >
                    {walkIn ? labels.submit : (labels.submit_confirmed || labels.title)}
                  </Button>
                }
              </div>
            </form>
          </Box>
        </Fade>
      </Modal>
    );
  }
}
